import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  alpha,
  useTheme,
} from '@mui/material';
import {
  TrendingUp,
  Security,
  Speed,
  SmartToy,
  Assessment,
  Notifications,
  CheckCircle,
  Forum,
  PictureAsPdf,
  Article,
  Workspaces,
} from '@mui/icons-material';

interface WelcomeStepProps {
  onNext: () => void;
  onBack: () => void;
  data: any;
  updateData: (data: any) => void;
}

const WelcomeStep: React.FC<WelcomeStepProps> = ({ onNext }) => {
  const theme = useTheme();

  const features = [
    {
      icon: <SmartToy />,
      title: 'AI-Powered Agents',
      description: '7 specialized agents working together to optimize your supply chain',
      color: theme.palette.primary.main,
    },
    {
      icon: <Workspaces />,
      title: 'Multi-Tenant Workspace',
      description: 'Your company’s data is isolated in its own tenant. Invite teammates and stay in control.',
      color: theme.palette.primary.dark,
    },
    {
      icon: <Notifications />,
      title: 'Daily Risk Briefings',
      description: 'Watchtower runs every morning to surface concentration, single-source, weather and news risks before they hit you.',
      color: theme.palette.warning.main,
    },
    {
      icon: <Article />,
      title: 'Knowledge Base + News',
      description: 'Drop in PDFs, DOCX, web URLs, or paste text — the agent cites your own documents and live news in every answer.',
      color: theme.palette.success.main,
    },
    {
      icon: <Forum />,
      title: 'Persistent Conversations',
      description: 'Every chat is saved, named, and resumable. Tool calls, citations and reasoning are preserved per thread.',
      color: theme.palette.info.main,
    },
    {
      icon: <PictureAsPdf />,
      title: 'One-Click PDF Export',
      description: 'Turn any briefing or analysis into a print-ready, board-grade PDF with citations.',
      color: theme.palette.error.main,
    },
    {
      icon: <Assessment />,
      title: 'Predictive Analytics',
      description: 'Forecast demand, identify risks, and prevent disruptions before they happen',
      color: theme.palette.secondary.main,
    },
    {
      icon: <Security />,
      title: 'Human-in-the-Loop',
      description: 'You stay in control — AI recommends, you decide, every action is auditable.',
      color: theme.palette.text.primary,
    },
  ];

  const benefits = [
    'Reduce inventory costs by up to 30%',
    'Improve forecast accuracy by 40%',
    'Decrease stockouts and overstock situations',
    'Identify supply chain risks early',
    'Optimize supplier relationships',
    'Make data-driven decisions faster',
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Transform Your Supply Chain
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          ASCIN combines cutting-edge AI with human expertise to create an adaptive,
          intelligent supply chain network that grows with your business.
        </Typography>
      </Box>

      {/* Features Grid */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {features.map((feature, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(feature.color, 0.1),
                      color: feature.color,
                      mr: 2,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Benefits Section */}
      <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            What You'll Achieve
          </Typography>
          <Grid container spacing={2}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircle
                    sx={{ color: theme.palette.success.main, mr: 1.5 }}
                  />
                  <Typography variant="body2">{benefit}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* What to Expect */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            What to Expect in This Setup
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  1
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Company Profile"
                secondary="Tell us about your business, industry, and goals"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  2
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Supply Chain Mapping"
                secondary="Map your suppliers, products, and distribution network"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  3
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Data Integration"
                secondary="Connect your existing systems (optional but recommended)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  4
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Human-in-the-Loop Setup"
                secondary="Learn how you'll collaborate with AI agents"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  5
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Agent Configuration"
                secondary="Customize your AI agents to match your needs"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          ⏱️ <strong>Time to complete:</strong> 10-15 minutes
          <br />
          💾 <strong>Save progress:</strong> You can pause and resume anytime
          <br />
          ✏️ <strong>Edit later:</strong> All settings can be updated from your dashboard
        </Typography>
      </Box>
    </Box>
  );
};

export default WelcomeStep;
